import {ChangeDetectionStrategy, Component, effect, Inject, inject} from '@angular/core';
import {MAT_DIALOG_DATA,MatDialogRef} from '@angular/material/dialog';
import {Observable} from 'rxjs';
import {BankModel} from 'src/app/shared/model/bank';
import {autocompleteAsyncValidator, autocompleteFormFilter, replayHttpQuery} from 'src/infrastructure/helper/autocomplete-form-filter';
import {CommissionReconciliationAdjustmentRequest, CommissionReconciliationData} from '../../models/commission-reconciliation';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {PayService} from 'src/app/shared/service/pay/pay.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {CommissionReconciliationService} from '../../service/commission-reconciliation.service';

@Component({
    selector: 'app-commission-reconciliation-popup',
    templateUrl: './commission-reconciliation-popup.component.html',
    styleUrl: './commission-reconciliation-popup.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CommissionReconciliationPopupComponent {
  isLoadingSpinner: boolean = false;
  commReconForm!: FormGroup;
  filteredBank$?: Observable<BankModel[]>;
  bankList$!: Observable<BankModel[]>;
  private snackBar = inject(MatSnackBar);
  displayWithBank = (bank: BankModel) => {
    return bank?.name || '';
  };
  private readonly dialogRef = inject<
    MatDialogRef<CommissionReconciliationPopupComponent, boolean>
  >(MatDialogRef<CommissionReconciliationPopupComponent>);

  constructor(
    @Inject(MAT_DIALOG_DATA) public serviceData: { checkedCount:number, checkedItems : CommissionReconciliationData[], dataSourceId:number },
    private fb: FormBuilder,
    private payService: PayService,
    private commissionReconciliationService: CommissionReconciliationService,
  ) {
    this.commReconForm = this.fb.group({
          bank: [null, Validators.required],
        });
        effect(() => {
          this.setAutocompleteComponents();
          this.setAutocompleteValidator();
      });
  }

  ngOnInit(): void {
      this.bankList$ = replayHttpQuery(this.payService.getBanks({ tramsDataSourceId: this.serviceData?.dataSourceId }));
      this.setAutocompleteComponents();
    }

  clearFields(...args: string[]): void {
    if (args.length) {
      args.forEach((field) => {
        this.commReconForm.controls[field].reset();
      });
    } else {
      this.commReconForm.reset();
    }
    this.setAutocompleteComponents();
  }

  setAutocompleteComponents(){
    this.filteredBank$ = autocompleteFormFilter(
      this.commReconForm.controls['bank'],
      'name',
      this.bankList$
    );
  }

  setAutocompleteValidator(): void {
    this.commReconForm.controls['bank'].setAsyncValidators(autocompleteAsyncValidator(this.bankList$));
}

  protected onConfirm() {
    if (this.commReconForm.invalid) {
      this.snackBar.open('No bank is selected to complete the update!', 'Dismiss', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
    } else{
      this.isLoadingSpinner = true;
      const bankId = this.commReconForm.controls['bank'].value?.id;
      const processCommissionReconciliationRequest =
        this.serviceData.checkedItems.map((item) => ({
          adxBookingId: item.fmsAdxBookingId ?? 0,
          ccProcessorId: item.processorId,
          processorRecordId: item.id,
          commissionAmount: item.commissionAmount,
        })
      );

      const request: CommissionReconciliationAdjustmentRequest = {
        processCommissionReconciliationRequest,
        bankId,
      };

      this.commissionReconciliationService.adjustmentCommissionReconciliations(request).subscribe(()=>{
        this.dialogRef.close(true);
        this.isLoadingSpinner = false;
        this.snackBar.open('Commission reconciliation adjustment completed successfully', 'Dismiss', {
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
        });
      });
    }
  }

  protected onCancel() {
    this.dialogRef.close(false);
  }
}
